// FILE: apps/admin/src/audit-catalog.ts
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Human-readable Russian catalog for Admin audit log actions and categories.
//   SCOPE: AuditActionMeta, AUDIT_ACTION_CATALOG, and describeAuditAction.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   AUDIT_ACTION_CATALOG - Dictionary mapping audit action keys to Russian titles and categories
//   AuditActionMeta - Metadata describing an audit action
//   describeAuditAction - Lookup helper returning Russian title, category, and tone for an audit action
// END_MODULE_MAP

export interface AuditActionMeta {
  action: string;
  title: string;
  category: 'finance' | 'users' | 'orders' | 'games' | 'security' | 'system';
  tone: 'cyan' | 'gold' | 'green' | 'violet' | 'coral' | 'blue';
}

export const AUDIT_ACTION_CATALOG: Record<string, AuditActionMeta> = {
  'admin.auth.login': {
    action: 'admin.auth.login',
    title: 'Вход оператора в админ-панель',
    category: 'security',
    tone: 'cyan',
  },
  'admin.session.revoked': {
    action: 'admin.session.revoked',
    title: 'Принудительный отзыв сессии',
    category: 'security',
    tone: 'coral',
  },
  'authorization.permission_denied': {
    action: 'authorization.permission_denied',
    title: 'Отказ доступа RBAC (403)',
    category: 'security',
    tone: 'coral',
  },
  'admin.action_batch.executed': {
    action: 'admin.action_batch.executed',
    title: 'Выполнение привилегированного действия',
    category: 'system',
    tone: 'violet',
  },
  'order.force_match': {
    action: 'order.force_match',
    title: 'Ручное зачисление оплаты заказа',
    category: 'orders',
    tone: 'gold',
  },
  'admin.order.cancel': {
    action: 'admin.order.cancel',
    title: 'Отмена заказа и возврат баланса',
    category: 'orders',
    tone: 'coral',
  },
  'admin.order.retry': {
    action: 'admin.order.retry',
    title: 'Повторная отправка заказа в Fragment',
    category: 'orders',
    tone: 'blue',
  },
  'admin.payment.match': {
    action: 'admin.payment.match',
    title: 'Ручная привязка платежа СБП к заказу',
    category: 'finance',
    tone: 'green',
  },
  'balance.adjust': {
    action: 'balance.adjust',
    title: 'Корректировка RUB-баланса пользователя',
    category: 'finance',
    tone: 'gold',
  },
  'balance.gram_adjust': {
    action: 'balance.gram_adjust',
    title: 'Корректировка игрового баланса GRAM',
    category: 'finance',
    tone: 'cyan',
  },
  'admin.user.balance': {
    action: 'admin.user.balance',
    title: 'Изменение баланса пользователя (₽)',
    category: 'finance',
    tone: 'gold',
  },
  'user.ban': {
    action: 'user.ban',
    title: 'Блокировка пользователя',
    category: 'security',
    tone: 'coral',
  },
  'user.unban': {
    action: 'user.unban',
    title: 'Разблокировка пользователя',
    category: 'security',
    tone: 'green',
  },
  'user.fish_action': {
    action: 'user.fish_action',
    title: 'Уход за рыбкой в аквариуме',
    category: 'users',
    tone: 'cyan',
  },
  'user.companion_grant': {
    action: 'user.companion_grant',
    title: 'Выдача рыбки-компаньона в коллекцию',
    category: 'users',
    tone: 'violet',
  },
  'user.companion_revoke': {
    action: 'user.companion_revoke',
    title: 'Изъятие рыбки-компаньона из коллекции',
    category: 'users',
    tone: 'coral',
  },
  'user.xp_adjust': {
    action: 'user.xp_adjust',
    title: 'Изменение опыта (XP) и стрика игрока',
    category: 'users',
    tone: 'blue',
  },
  'user.role_set': {
    action: 'user.role_set',
    title: 'Назначение RBAC ролей оператора',
    category: 'security',
    tone: 'violet',
  },
  'user.verify_toggle': {
    action: 'user.verify_toggle',
    title: 'Переключение верификации профиля',
    category: 'users',
    tone: 'green',
  },
  'user.send_dm': {
    action: 'user.send_dm',
    title: 'Отправка личного сообщения от бота',
    category: 'users',
    tone: 'cyan',
  },
  'user.ref_code_set': {
    action: 'user.ref_code_set',
    title: 'Установка кастомного реферального кода',
    category: 'users',
    tone: 'violet',
  },
  'user.reset_profile': {
    action: 'user.reset_profile',
    title: 'Сброс кастомного ника / аватарки',
    category: 'security',
    tone: 'coral',
  },
  'support.user_report.resolve': {
    action: 'support.user_report.resolve',
    title: 'Решение по жалобе на игрока',
    category: 'security',
    tone: 'green',
  },
  'support.ticket.reply': {
    action: 'support.ticket.reply',
    title: 'Ответ оператора в тикете поддержки',
    category: 'users',
    tone: 'green',
  },
  'appeal.approved': {
    action: 'appeal.approved',
    title: 'Одобрение апелляции и снятие бана',
    category: 'security',
    tone: 'green',
  },
  'appeal.rejected': {
    action: 'appeal.rejected',
    title: 'Отклонение апелляции бана',
    category: 'security',
    tone: 'coral',
  },
  'banner.create': {
    action: 'banner.create',
    title: 'Создание промо-баннера Mini App',
    category: 'system',
    tone: 'cyan',
  },
  'banner.update': {
    action: 'banner.update',
    title: 'Обновление промо-баннера Mini App',
    category: 'system',
    tone: 'blue',
  },
  'banner.delete': {
    action: 'banner.delete',
    title: 'Удаление промо-баннера Mini App',
    category: 'system',
    tone: 'coral',
  },
  'broadcast.create': {
    action: 'broadcast.create',
    title: 'Запуск массовой рассылки в боте',
    category: 'system',
    tone: 'violet',
  },
  'quest.update': {
    action: 'quest.update',
    title: 'Изменение параметров задания (Квеста)',
    category: 'games',
    tone: 'gold',
  },
  'admin.setting.update': {
    action: 'admin.setting.update',
    title: 'Обновление курса и настроек экономики',
    category: 'finance',
    tone: 'gold',
  },
  'config.spend_tiers_update': {
    action: 'config.spend_tiers_update',
    title: 'Обновление уровней сниженной комиссии за оборот',
    category: 'finance',
    tone: 'gold',
  },
  'user.commission_update': {
    action: 'user.commission_update',
    title: 'Изменение персональной комиссии пользователя',
    category: 'finance',
    tone: 'violet',
  },
  'referral.code_create': {
    action: 'referral.code_create',
    title: 'Создание настраиваемого реферального / промо-кода',
    category: 'finance',
    tone: 'green',
  },
  'referral.code_update': {
    action: 'referral.code_update',
    title: 'Обновление настраиваемого реферального / промо-кода',
    category: 'finance',
    tone: 'cyan',
  },
  'referral.code_delete': {
    action: 'referral.code_delete',
    title: 'Удаление настраиваемого реферального / промо-кода',
    category: 'finance',
    tone: 'coral',
  },
  'treasury.drift_reconcile': {
    action: 'treasury.drift_reconcile',
    title: 'Сверка и выравнивание расхождения леджера (DRIFT)',
    category: 'finance',
    tone: 'green',
  },
  'quest.create': {
    action: 'quest.create',
    title: 'Создание нового задания (Квеста)',
    category: 'games',
    tone: 'green',
  },
  'fragment.session_save': {
    action: 'fragment.session_save',
    title: 'Сохранение и шифрование cookies сессии Fragment',
    category: 'security',
    tone: 'cyan',
  },
  'referral.user_code_perks_update': {
    action: 'referral.user_code_perks_update',
    title: 'Обновление бонусов личного реферального кода игрока',
    category: 'finance',
    tone: 'violet',
  },
  'user.ref_code_update': {
    action: 'user.ref_code_update',
    title: 'Изменение личного реферального кода игрока',
    category: 'users',
    tone: 'violet',
  },
  'admin.me.balance_adjust': {
    action: 'admin.me.balance_adjust',
    title: 'Быстрое пополнение собственного RUB-баланса оператора',
    category: 'finance',
    tone: 'gold',
  },
  'admin.me.gram_adjust': {
    action: 'admin.me.gram_adjust',
    title: 'Быстрое пополнение собственного GRAM-баланса оператора',
    category: 'finance',
    tone: 'cyan',
  },
  'admin.step_up.requested': {
    action: 'admin.step_up.requested',
    title: 'Запрос кода подтверждения Step-Up (Telegram DM)',
    category: 'security',
    tone: 'cyan',
  },
  'admin.step_up.verified': {
    action: 'admin.step_up.verified',
    title: 'Успешное подтверждение Step-Up OTP',
    category: 'security',
    tone: 'green',
  },
  'admin.step_up.failed': {
    action: 'admin.step_up.failed',
    title: 'Неудачная попытка подтверждения Step-Up OTP',
    category: 'security',
    tone: 'coral',
  },
  'config.pricing_lock_apply': {
    action: 'config.pricing_lock_apply',
    title: 'Фиксация актуального курса Fragment и CoinGecko',
    category: 'finance',
    tone: 'green',
  },
  'config.pricing_candidate_dismiss': {
    action: 'config.pricing_candidate_dismiss',
    title: 'Оставлен текущий зафиксированный курс витрины',
    category: 'finance',
    tone: 'blue',
  },
  'catalog.sync': {
    action: 'catalog.sync',
    title: 'Ручная синхронизация прайса B2B каталога',
    category: 'system',
    tone: 'cyan',
  },
  'catalog.category.update': {
    action: 'catalog.category.update',
    title: 'Обновление базовой наценки / публикации категории',
    category: 'finance',
    tone: 'gold',
  },
  'catalog.product.update': {
    action: 'catalog.product.update',
    title: 'Обновление карточки товара каталога',
    category: 'system',
    tone: 'blue',
  },
  'catalog.offer.update': {
    action: 'catalog.offer.update',
    title: 'Обновление наценки / публикации SKU каталога',
    category: 'finance',
    tone: 'green',
  },
  'catalog.bulk_publish': {
    action: 'catalog.bulk_publish',
    title: 'Массовая публикация товаров и SKU витрины',
    category: 'system',
    tone: 'green',
  },
  'catalog.local_offer.create': {
    action: 'catalog.local_offer.create',
    title: 'Создание локального SKU (Свой склад)',
    category: 'system',
    tone: 'cyan',
  },
  'catalog.vouchers.upload': {
    action: 'catalog.vouchers.upload',
    title: 'Загрузка зашифрованных ПИН-кодов в автосклад',
    category: 'security',
    tone: 'green',
  },
  'catalog.order.retry_now': {
    action: 'catalog.order.retry_now',
    title: 'Принудительный повтор доставки B2B заказа',
    category: 'orders',
    tone: 'blue',
  },
};

// START_CONTRACT: describeAuditAction
//   PURPOSE: Return Russian label and category metadata for an audit log action key.
//   INPUTS: action string
//   OUTPUTS: AuditActionMeta
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: describeAuditAction
export function describeAuditAction(action: string): AuditActionMeta {
  // START_BLOCK_DESCRIBE_AUDIT_ACTION
  return (
    AUDIT_ACTION_CATALOG[action] ?? {
      action,
      title: action,
      category: 'system',
      tone: 'blue',
    }
  );
  // END_BLOCK_DESCRIBE_AUDIT_ACTION
}
